import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import {
  getLocalTimeZone,
  now,
  toZoned,
  ZonedDateTime
} from "@internationalized/date";
import { Cubit } from "blac-next";
import FunnelKey from "src/constants/funnelKey";
import { MAX_TIMEOUT } from "src/constants/misc";
import {
  addSentryBreadcrumb,
  logSentryBreadcrumb
} from "src/lib/addSentryBreadcrumb";
import { featureFlags } from "src/lib/featureFlags";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { apiMiddleware, subscriptionState, userState } from "src/state/state";
import type {
  SavingIndicatorConfig,
  SignupCustomCampaign,
  SignupCustomPageParameters,
  SignupCustomState,
  SignupCustomStep
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import {
  SignupCustomCampaignName,
  SignupCustomStepType
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import { campaignInstances } from "../campaigns/SignupCustomCampaignGenericInstances";

type SignupCustomProps = {
  parameters: SignupCustomPageParameters;
  options: {
    preview?: boolean;
    demo?: boolean;
    disableInit?: boolean;
  };
};

export default class SignupCustomBloc extends Cubit<
  SignupCustomState,
  SignupCustomProps
> {
  defaultDependencySelector = (s: SignupCustomState) => [s];
  checkForDataComplete = true;
  previewMode = false;
  isDemoFunnel = false;

  constructor(options: SignupCustomProps) {
    const { parameters } = options;
    const campaign = parameters.campaign ?? "";
    const step = parameters.step ?? "";

    // set initial state
    super({
      campaign,
      step,
      currentStepValid: false,
      key: 0
    });

    this.isDemoFunnel = Boolean(options.options.demo);

    // set preview mode
    this.previewMode = options.options.preview ?? false;
    if (options.options.disableInit !== true) {
      this.init();
    }
  }

  getAvailableCampaigns(): SignupCustomCampaign[] {
    return campaignInstances();
  }

  /**
   * Initialize the bloc
   */
  init(): void {
    void this.checkFunnelAvailability();
    void this.setInitialStep();
  }

  checkFunnelAvailability = async (): Promise<void> => {
    const campaign = this.getActiveCampaign();
    let featureFlagEnabledValid = true;
    let userIsSsoValid = true;
    let funnelIsLaunched = true;
    const featureFlagName = campaign.availabilityChecks?.featureFlag;

    // check release date
    if (campaign.availabilityChecks?.releaseDate) {
      const timeZone = getLocalTimeZone();
      const currentDateTime = now(timeZone);
      const releaseDateTime = toZoned(
        campaign.availabilityChecks.releaseDate,
        timeZone
      );

      if (currentDateTime < releaseDateTime) {
        const ttl =
          releaseDateTime.toDate().getTime() -
          currentDateTime.toDate().getTime();
        const cap = Math.min(ttl, MAX_TIMEOUT);

        // re-check availability after the timeout
        setTimeout(() => {
          void this.checkFunnelAvailability();
        }, cap + 1000); // add 1 second to make sure we're past the release time

        // set state now so that we don't have to wait for the async calls below
        this.patch({
          isAvailable: false
        });

        funnelIsLaunched = false;
      }
    }

    // check feature flag
    let isAvailable = true;
    if (featureFlagName) {
      await featureFlags.loadBackendFlags();
      featureFlagEnabledValid = Boolean(
        featureFlags.getRemoteFlag(featureFlagName)
      );
    }

    // check SSO email verified
    if (campaign.availabilityChecks?.blockAnonymousUsers) {
      await userState.loadUserInfo();
      userIsSsoValid = userState.userHasEmailVerified;
    }

    isAvailable = featureFlagEnabledValid && userIsSsoValid && funnelIsLaunched;

    if (!isAvailable) {
      reportErrorSentry(new Error("Campaign unavailable"), {
        featureFlagEnabledValid,
        userIsSsoValid
      });
    }

    this.patch({
      isAvailable: isAvailable
    });
  };

  get enableLogging(): boolean {
    return false;
  }

  log = (message: string, ...add: unknown[]): void => {
    if (this.enableLogging) {
      // eslint-disable-next-line no-console
      console.log(`[SignupCustomBloc] ${message}`, ...add);
    }
    logSentryBreadcrumb("signupCustomBloc", message);
  };

  /**
   * Grant app access to the user and log the action
   * @param reason - The reason for granting access (for logging purposes)
   */
  private grantAppAccess(reason: string): void {
    this.log(`${reason} - setting hasAppAccess to true`);
    StorageController.setHasAppAccess(true);
  }

  getActiveCampaign = (): SignupCustomCampaign => {
    const { campaign } = this.state;

    const instances = this.getAvailableCampaigns();

    const activeCampaign = instances.find(
      (customCampaign) =>
        customCampaign.name === (campaign as SignupCustomCampaignName) ||
        customCampaign.funnelKey === (campaign as FunnelKey)
    );

    if (!activeCampaign) {
      return {
        funnelKey: FunnelKey.test,
        name: SignupCustomCampaignName.Default,
        prettyName: "Default",
        steps: () => []
      };
    }

    return activeCampaign;
  };

  /**
   * Get the campaign steps from the campaignStepsMap
   * @returns the campaign steps
   */
  getCampaignSteps = (): SignupCustomStep[] => {
    try {
      return this.getActiveCampaign().steps();
    } catch (error) {
      reportErrorSentry(error);
      return [];
    }
  };

  /**
   * Get the campaign pretty name from the campaignPrettyNameMap
   * @param campaign the campaign name
   * @returns the campaign pretty name
   */
  getCampaignPrettyName = (): string => {
    return this.getActiveCampaign().prettyName;
  };

  /**
   * Get the funnel key from the funnelKeyMap
   * @param campaign the campaign name
   * @returns the funnel key
   */
  getCampaignFunnelKey = (): FunnelKey => {
    return this.getActiveCampaign().funnelKey;
  };

  /**
   * Set the initial step
   * If there are no steps, clear the state
   * If there are steps, go to the first incomplete step
   * If there are no incomplete steps, go to the success step
   */
  setInitialStep = async (): Promise<void> => {
    // Clear caches to ensure fresh data after redirect/navigation
    apiMiddleware.clearAll();
    userState.clearAnsweredQuestionnairesCache();

    // stop there is no campaignSteps
    if (!this.campaignSteps) {
      return;
    }

    if (this.previewMode) {
      const currentStep = this.campaignSteps.find(
        (s) => s.path === this.state.step
      );

      if (currentStep) {
        this.patch({
          currentStepValid: true
        });
        return;
      }
    }

    const firstNotCompleteStep = await this.findFirstIncompleteStep();

    // go to the first incomplete step
    if (firstNotCompleteStep) {
      this.patch({
        step: firstNotCompleteStep.path,
        currentStepValid: true,
        stepForced: false
      });
      return;
    }

    // if firstNotCompleteStep is undefined, that means that there are no steps, or that all steps are complete
    // if there are steps, go to the success step
    const successStep = this.campaignSteps.find(
      (s) => s.type === SignupCustomStepType.SuccessPage
    );

    if (successStep) {
      // If we're going to the success step because all steps are complete, mark as having app access
      this.grantAppAccess("All steps complete");

      this.patch({
        step: successStep.path,
        currentStepValid: true,
        stepForced: false
      });
      return;
    }

    // if there are no steps, clear the state
    this.patch({
      step: "",
      currentStepValid: true,
      stepForced: false
    });
  };

  /**
   * Get the current step based on the step in the state
   * @returns the current step or undefined if there are no steps in the state
   */
  getNextStep = (): SignupCustomStep | undefined => {
    const campaignSteps = this.getActiveCampaign().steps();
    const { step } = this.state;

    const currentStepIndex = campaignSteps.findIndex((s) => s.path === step);
    return campaignSteps[currentStepIndex + 1];
  };

  /**
   * Check if the step is complete
   * @param step the step to check
   * @returns true if the step is complete, false if it is not
   */
  checkStepDataComplete = async (step: SignupCustomStep): Promise<boolean> => {
    this.log("checkStepDataComplete", {
      step,
      checkForDataComplete: this.checkForDataComplete,
      alreadyCompleted: this.dataCompletedSteps.has(step.path),
      answered: userState.state
    });
    if (!this.checkForDataComplete) {
      return false;
    }

    if (typeof step.dataComplete === "undefined") {
      return false;
    }

    try {
      const complete = await step.dataComplete();
      this.log("step.dataComplete()", { step, complete });
      return complete;
    } catch (error) {
      this.log("Error checking step data complete", error);
      reportErrorSentry(error);
    }

    return false;
  };
  dataCompletedSteps = new Set<string>();

  /**
   * Check if the current step is complete
   * @returns true if the current step is complete, false if it is not
   */
  checkCurrentStepDataComplete = async (): Promise<boolean> => {
    if (!this.currentStep) {
      return false;
    }

    try {
      const complete = await this.checkStepDataComplete(this.currentStep);
      return complete;
    } catch (error) {
      reportErrorSentry(error);
    }

    return false;
  };

  nextStep = async (): Promise<void> => {
    const { currentStep } = this;
    this.log("current", currentStep);

    if (currentStep?.beforeNextStep) {
      await currentStep.beforeNextStep();
    }

    const nextStepRequired = await this.findFirstIncompleteStep();
    this.log("nextStepRequired", nextStepRequired);

    const lastSuccessStep = this.campaignSteps?.findLast(
      (s) => s.type === SignupCustomStepType.SuccessPage
    );

    const nextStep = nextStepRequired ?? lastSuccessStep;

    this.log("nextStep", nextStep);

    if (nextStep && nextStep.path !== this.state.step) {
      // If we're moving to the final success step and all required steps are complete,
      // mark as having app access
      if (nextStep === lastSuccessStep && !nextStepRequired) {
        this.grantAppAccess("Funnel completed");
      }

      this.patch({
        step: nextStep.path,
        currentStepValid: true,
        key: this.state.key + 1,
        stepForced: false,
        transitionPending: true
      });
      return;
    } else {
      addSentryBreadcrumb(
        "funnel",
        `No next step found, step: ${this.state.step}`
      );
    }
  };

  /**
   * Called when the new step has finished rendering to complete the transition
   */
  public readonly completeTransition = (): void => {
    if (this.state.transitionPending) {
      this.patch({
        transitionPending: false
      });
    }
    this.completeSaving();
  };

  __unsafe__forceStep = (step: SignupCustomStep): void => {
    this.checkForDataComplete = false;
    this.patch({
      step: step.path,
      currentStepValid: true,
      key: this.state.key + 1,
      stepForced: true
    });
    this.checkForDataComplete = true;
  };

  /**
   * Checks if all previous steps are complete
   * @returns true if all previous steps are complete, false if any are incomplete
   */
  checkAllPreviousStepsComplete = async (): Promise<boolean> => {
    const steps = this.campaignSteps;

    // if this is the first step, it is always complete
    if (this.currentStepIndex === 0 || !steps) {
      return true;
    }

    // check all previous steps in parallel for better performance
    const previousSteps = steps.slice(0, this.currentStepIndex);
    const results = await Promise.all(
      previousSteps.map((step) => this.checkStepDataComplete(step))
    );

    // return true if all steps are complete
    return results.every((complete) => complete);
  };

  /**
   * Find the first incomplete step based on the campaignSteps in the state
   * @returns the first incomplete step or undefined if all steps are complete
   */
  findFirstIncompleteStep = async (): Promise<SignupCustomStep | undefined> => {
    // make sure all data is loaded
    await userState.loadRequiredAppUserData();
    const steps = this.campaignSteps;

    if (!steps) {
      return undefined;
    }

    // Run all dataComplete checks in parallel for better performance.
    // API calls will be deduped by apiMiddleware.
    // Promise.all preserves order: results[i] corresponds to steps[i]
    const results = await Promise.all(
      steps.map(async (step) => ({
        step,
        complete: await this.checkStepDataComplete(step)
      }))
    );

    // find() returns the first match in array order, so this returns
    // the first incomplete step according to the original steps sequence
    const firstIncomplete = results.find((r) => !r.complete);
    if (firstIncomplete) {
      this.log("First incomplete step found", firstIncomplete.step);
      return firstIncomplete.step;
    }

    return undefined;
  };

  /**
   * Check if the final success step is complete
   */
  checkFinalSuccessStepCompleted = async (): Promise<boolean> => {
    const steps = this.campaignSteps;
    if (!steps) {
      return false;
    }

    const successStep = steps.findLast(
      (s) => s.type === SignupCustomStepType.SuccessPage
    );

    if (!successStep) {
      return false;
    }

    const stepDataComplete = await this.checkStepDataComplete(successStep);

    // If the final success step is complete, mark as having app access
    if (stepDataComplete) {
      this.grantAppAccess("Final success step completed");
    }

    return stepDataComplete;
  };

  /**
   * Get the index of the step in the campaignSteps array
   * @param step the step to get the index of
   * @returns the index of the step in the campaignSteps array, or -1 if the step is not found
   */
  getStepIndex = (step: SignupCustomStep): number => {
    const { campaignSteps } = this;
    if (!campaignSteps) {
      return -1;
    }

    return campaignSteps.findIndex((s) => s.path === step.path);
  };

  /**
   * Get the path for the step
   * @param step the step to get the path for
   * @returns the path for the step
   */
  getPathForStep = (step: SignupCustomStep): string => {
    const { campaign } = this.state;
    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();
    return `/signup/${campaign}/${step.path}?${queryString}`;
  };

  /**
   * Load subscriptions, check for draft or active subscriptions with a membership purchase item and redirect to the funnel
   * The user probably ended up in the wrong funnel, so we redirect them to the correct one
   */
  checkForOtherFunnelSubscriptions = async (): Promise<void> => {
    try {
      await subscriptionState.loadAllSubscriptions({
        cachedSubscriptionsAcceptable: true
      });

      const membershipSubscription = subscriptionState
        .filterAllSubscriptions({
          status: [
            SubscriptionDetailsResponse.status.DRAFT,
            SubscriptionDetailsResponse.status.ACTIVE,
            SubscriptionDetailsResponse.status.IN_REVIEW,
            SubscriptionDetailsResponse.status.FINISHED,
            SubscriptionDetailsResponse.status.PAUSED
          ]
        })
        .find((subscription) =>
          subscription.purchaseItems.some(
            (item) => item.sku === "base_membership"
          )
        );

      const subFunnelKey = membershipSubscription?.funnel as
        | FunnelKey
        | undefined;

      const subFunnelKeyMatchesCurrent =
        subFunnelKey === this.campaignFunnelKey;

      if (
        !subFunnelKeyMatchesCurrent &&
        membershipSubscription &&
        subFunnelKey
      ) {
        window.location.replace(`/app?from=funnel&funnelKey=${subFunnelKey}`);
      }
    } catch (error) {
      reportErrorSentry(
        new Error("Error checking for other funnel subscriptions", {
          cause: error
        })
      );
    }
  };

  /**
   * Change progress bar visibility
   */
  public readonly setProgressBarVisibility = (visible: boolean): void => {
    this.patch({
      hideProgressBar: !visible
    });
  };

  private static readonly CHECKOUT_TRANSITION_IMG =
    "https://cdn.sanity.io/images/g38rxyoc/production/76a7c3281e83c88cb8a42ad38bf19da3ed58ae4d-981x822.png?w=440";

  /**
   * Show a saving indicator overlay with optional custom text.
   * By default, if next step is checkout, shows special transition UI with image.
   * Set forStepTransition to false to disable this behavior.
   */
  public readonly showSaving = (config: SavingIndicatorConfig = {}): void => {
    const forStepTransition = config.forStepTransition !== false;
    let finalConfig = config;

    if (forStepTransition) {
      const nextStep = this.getNextStep();
      if (nextStep?.path === "checkout") {
        finalConfig = {
          ...config,
          text: translate("processingInformation"),
          img: SignupCustomBloc.CHECKOUT_TRANSITION_IMG
        };
      }
    }

    this.patch({
      savingIndicator: finalConfig
    });
  };

  /**
   * Mark the saving indicator as complete (triggers completion animation)
   */
  public readonly completeSaving = (): void => {
    if (this.state.savingIndicator) {
      this.patch({
        savingIndicator: {
          ...this.state.savingIndicator,
          complete: true
        }
      });
    }
  };

  /**
   * Hide the saving indicator overlay
   */
  public readonly hideSaving = (): void => {
    this.patch({
      savingIndicator: null
    });
  };

  /**
   * Get the current step based on the step in the state,
   * or undefined if there are no steps in the state
   */
  get currentStep(): SignupCustomStep | undefined {
    const { campaignSteps } = this;
    const { step } = this.state;

    if (!campaignSteps) {
      return undefined;
    }

    return campaignSteps.find((s) => s.path === step);
  }

  /**
   * Get the index of the current step in the campaignSteps array,
   * or -1 if there are no steps in the state
   */
  get currentStepIndex(): number {
    const { currentStep } = this;
    if (!currentStep) {
      return -1;
    }

    return this.getStepIndex(currentStep);
  }

  /**
   * Check if the campaign name is valid
   */
  get campaignNameIsValid(): boolean {
    const { campaign } = this.state;
    return Object.values(SignupCustomCampaignName).includes(
      campaign as SignupCustomCampaignName
    );
  }

  get campaignReleaseDate(): ZonedDateTime | undefined {
    const campaign = this.getActiveCampaign();
    if (!campaign.availabilityChecks?.releaseDate) {
      return undefined;
    }
    return toZoned(campaign.availabilityChecks.releaseDate, getLocalTimeZone());
  }

  /**
   * Get the pretty name of the campaign
   */
  get campaignPrettyName(): string {
    return this.getCampaignPrettyName();
  }

  /**
   * Get the funnel key for the campaign
   */
  get campaignFunnelKey(): FunnelKey {
    return this.getCampaignFunnelKey();
  }

  /**
   * Get the steps for the current campaign
   */
  get campaignSteps(): SignupCustomStep[] | undefined {
    return this.getCampaignSteps();
  }

  get coBrandedLogo(): string | undefined {
    const campaign = this.getActiveCampaign();
    return campaign.logo;
  }

  get coBrandedLogoSize(): SignupCustomCampaign["logoSize"] | undefined {
    const campaign = this.getActiveCampaign();
    return campaign.logoSize;
  }

  get customLogo(): string | undefined {
    const campaign = this.getActiveCampaign();
    return campaign.customLogo;
  }
}
